import { useCallback, useEffect, useState } from 'react';
import { api, ApiError } from '../api/client';
import { Banner, EmptyState, Spinner } from '../components/States';
import { Stars, StarsInput } from '../components/Stars';
import { useAuth } from '../hooks/useAuth';
import { dateTime } from '../lib/format';
import type { Feedback, MuralResponse } from '../api/types';

const ROTULO_STATUS: Record<string, string> = {
  pendente: 'Em análise',
  aprovado: 'Publicado',
  recusado: 'Não publicado',
};

/**
 * Feedbacks dos clientes: o mural do que já foi aprovado e, para quem está
 * logado, o próprio feedback.
 *
 * O mural é público de propósito — é reputação, e reputação que só o dono lê
 * não serve para nada. O formulário é que exige conta.
 */
export function FeedbacksPage() {
  const { user, loading: carregandoConta, loginUrl } = useAuth();
  const [mural, setMural] = useState<MuralResponse | null>(null);
  const [meu, setMeu] = useState<Feedback | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    try {
      setMural(await api.mural());
      setErro(null);
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : 'Não consegui carregar os feedbacks.');
    }
  }, []);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  useEffect(() => {
    if (!user) return;
    void api
      .meuFeedback()
      .then((r) => setMeu(r.feedback))
      .catch(() => setMeu(null));
  }, [user]);

  return (
    <>
      <div className="panel">
        <h2>Feedbacks dos clientes</h2>
        <p className="muted">O que quem já comprou achou do atendimento e da entrega.</p>

        {erro && <Banner kind="error">{erro}</Banner>}

        {!mural ? (
          <Spinner label="Carregando…" />
        ) : (
          <>
            <Resumo mural={mural} />
            {mural.items.length === 0 ? (
              <EmptyState>Ainda não há feedbacks publicados. Seja o primeiro a avaliar.</EmptyState>
            ) : (
              <ul className="mural">
                {mural.items.map((f) => (
                  <li key={f.id} className="mural-item">
                    {/* Duas linhas de cabeçalho, e não uma: num cartão estreito
                        avatar, nome, SteamID e cinco estrelas na mesma linha
                        quebram no meio do nome. */}
                    <div className="mural-quem">
                      {f.avatarUrl ? (
                        <img src={f.avatarUrl} alt="" />
                      ) : (
                        <span className="mural-avatar-vazio" aria-hidden>
                          {(f.personaName ?? 'C').charAt(0).toUpperCase()}
                        </span>
                      )}
                      <div className="mural-quem-texto">
                        {/* O nome leva ao perfil na Steam: é o que permite a
                            quem lê conferir que a pessoa existe. */}
                        {f.steamId ? (
                          <a
                            className="mural-autor"
                            href={`https://steamcommunity.com/profiles/${f.steamId}`}
                            target="_blank"
                            rel="noreferrer noopener"
                          >
                            {f.personaName ?? 'Cliente'}
                          </a>
                        ) : (
                          <b className="mural-autor">{f.personaName ?? 'Cliente'}</b>
                        )}
                        {f.steamId && <span className="mural-steamid">{f.steamId}</span>}
                      </div>
                    </div>

                    <div className="mural-linha">
                      <Stars value={f.rating} size="sm" />
                      <time className="mural-data" dateTime={f.createdAt}>
                        {dateTime(f.createdAt)}
                      </time>
                    </div>

                    {f.comment && <p className="mural-texto">{f.comment}</p>}

                    {f.storeReply && (
                      <div className="mural-resposta">
                        <b>Resposta da loja</b>
                        <p>{f.storeReply}</p>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>

      {carregandoConta ? null : user ? (
        <MeuFeedback
          atual={meu}
          onSalvo={(f) => {
            setMeu(f);
            void carregar();
          }}
          onApagado={() => {
            setMeu(null);
            void carregar();
          }}
        />
      ) : (
        <div className="panel">
          <h3>Deixe o seu feedback</h3>
          <p className="muted">Entre com a Steam para avaliar a loja.</p>
          <a className="btn primary" href={loginUrl}>
            Entrar com a Steam
          </a>
        </div>
      )}
    </>
  );
}

/**
 * Resumo da reputação.
 *
 * Fica contido à esquerda em vez de esticar na largura da tela: barras de
 * distribuição com dois palmos de comprimento não ficam mais informativas, só
 * mais largas — e afastam o número da barra que ele mede.
 */
function Resumo({ mural }: { mural: MuralResponse }) {
  const { average, total, distribution } = mural.summary;
  if (!total || average === null) return null;

  // Quantos deram 4 ou 5. É o número que um cliente procura quando bate o olho
  // na média — a média sozinha não diz se houve nota baixa no meio.
  const satisfeitos = (distribution[4] ?? 0) + (distribution[5] ?? 0);
  const recomendam = Math.round((satisfeitos / total) * 100);

  return (
    <div className="mural-resumo">
      <div className="mural-nota">
        <b>{average.toFixed(1).replace('.', ',')}</b>
        <Stars value={average} size="lg" />
        <span className="mural-nota-total">
          {total} {total === 1 ? 'avaliação' : 'avaliações'}
        </span>
      </div>

      <div className="mural-barras">
        {([5, 4, 3, 2, 1] as const).map((n) => {
          const qtd = distribution[n] ?? 0;
          return (
            <div className="mural-barra" key={n}>
              <span className="mural-barra-nota">{n}</span>
              <div className="mural-barra-trilho">
                <div className="mural-barra-preenchida" style={{ width: `${total ? (qtd / total) * 100 : 0}%` }} />
              </div>
              <span className="mural-barra-qtd">{qtd}</span>
            </div>
          );
        })}
      </div>

      <div className="mural-recomendam">
        <b>{recomendam}%</b>
        <span>
          deram 4 ou 5<br />estrelas
        </span>
      </div>
    </div>
  );
}

function MeuFeedback({
  atual,
  onSalvo,
  onApagado,
}: {
  atual: Feedback | null;
  onSalvo: (f: Feedback) => void;
  onApagado: () => void;
}) {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [editando, setEditando] = useState(false);
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  // O rascunho parte do que já está salvo; sem isto, abrir a edição limpava a
  // avaliação anterior e a pessoa reescrevia do zero.
  useEffect(() => {
    setRating(atual?.rating ?? 0);
    setComment(atual?.comment ?? '');
  }, [atual]);

  async function salvar() {
    setOcupado(true);
    setErro(null);
    try {
      const { feedback } = await api.salvarFeedback({ rating, comment: comment || undefined });
      onSalvo(feedback);
      setEditando(false);
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : 'Não consegui salvar o seu feedback.');
    } finally {
      setOcupado(false);
    }
  }

  async function apagar() {
    if (!window.confirm('Apagar o seu feedback? Ele sai do mural.')) return;
    setOcupado(true);
    try {
      await api.apagarFeedback();
      setRating(0);
      setComment('');
      setEditando(false);
      onApagado();
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : 'Não consegui apagar.');
    } finally {
      setOcupado(false);
    }
  }

  if (atual && !editando) {
    return (
      <div className="panel">
        <h3>O seu feedback</h3>
        <div className="meu-feedback">
          <div className="mural-cabeca">
            <Stars value={atual.rating} />
            <span className={`feedback-status ${atual.status}`}>{ROTULO_STATUS[atual.status] ?? atual.status}</span>
            <span className="dim">{dateTime(atual.updatedAt)}</span>
          </div>
          {atual.comment && <p className="mural-texto">{atual.comment}</p>}

          {atual.status === 'pendente' && (
            <Banner kind="info">Recebemos. Assim que for conferido, aparece no mural acima.</Banner>
          )}
          {atual.status === 'recusado' && (
            <Banner kind="warn">
              Este feedback não foi publicado. Motivo: {atual.rejectReason ?? 'não informado'}. Você pode editar e
              enviar de novo.
            </Banner>
          )}
          {atual.storeReply && (
            <div className="mural-resposta">
              <b>Resposta da loja</b>
              <p>{atual.storeReply}</p>
            </div>
          )}
        </div>

        {erro && <Banner kind="error">{erro}</Banner>}
        <div className="actions-row">
          <button className="btn ghost" onClick={() => setEditando(true)}>
            Editar
          </button>
          <button className="btn ghost" onClick={() => void apagar()} disabled={ocupado}>
            Apagar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="panel">
      <h3>{atual ? 'Editar o seu feedback' : 'Deixe o seu feedback'}</h3>
      <p className="muted">
        {atual
          ? 'Ao salvar, a avaliação volta para conferência antes de aparecer de novo no mural.'
          : 'Uma avaliação por cliente. Você pode editar depois quando quiser.'}
      </p>

      {erro && <Banner kind="error">{erro}</Banner>}

      <div className="form">
        <label className="field">
          <span>
            Sua nota <span className="req">obrigatório</span>
          </span>
          <StarsInput value={rating} onChange={setRating} disabled={ocupado} />
        </label>

        <label className="field">
          <span>Comentário</span>
          <textarea
            rows={5}
            maxLength={1500}
            placeholder="Como foi a compra? O que funcionou bem, o que poderia melhorar?"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
          <small>{comment.length}/1500 · opcional, mas é o que ajuda quem está decidindo.</small>
        </label>

        <div className="actions-row">
          <button className="btn primary" onClick={() => void salvar()} disabled={ocupado || rating < 1}>
            {ocupado ? 'Enviando…' : atual ? 'Salvar alterações' : 'Enviar feedback'}
          </button>
          {atual && (
            <button className="btn ghost" onClick={() => setEditando(false)} disabled={ocupado}>
              Cancelar
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
